'use client'

import React, { useState, useEffect, useRef } from 'react'

type SuggestionItem = {
  id: number
  title: string
  slug: string
  price: number | null
}

type Props = {
  value: string
  caret: number
  onPick: (newText: string, pickedWord?: string) => void
  onHasMatchesChange?: (hasMatches: boolean) => void
}

/**
 * Extracts the current word token around/before the caret.
 * Returns { token, start, end } bounds in the value string.
 */
function getCurrentToken(value: string, caret: number): { token: string; start: number; end: number } {
  if (!value || caret < 0) return { token: '', start: 0, end: 0 }

  const textBefore = value.slice(0, caret)
  const lastSpace = Math.max(textBefore.lastIndexOf(' '), textBefore.lastIndexOf('\n'))
  const start = lastSpace === -1 ? 0 : lastSpace + 1
  const token = textBefore.slice(start).trim()

  return { token, start, end: caret }
}

export const ChatInputSuggestions: React.FC<Props> = ({
  value,
  caret,
  onPick,
  onHasMatchesChange,
}) => {
  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([])
  const [activeToken, setActiveToken] = useState('')
  const lastPickedRef = useRef<string | null>(null)

  const { token, start, end } = getCurrentToken(value, caret)

  useEffect(() => {
    if (token.length < 3 || token === lastPickedRef.current) {
      setSuggestions([])
      setActiveToken('')
      onHasMatchesChange?.(false)
      return
    }

    const timeoutId = setTimeout(async () => {
      try {
        const res = await fetch(`/api/search/suggest?q=${encodeURIComponent(token)}&limit=3`)
        if (res.ok) {
          const data = await res.json()
          const matched = data.products || []
          setSuggestions(matched)
          setActiveToken(token)
          onHasMatchesChange?.(matched.length > 0)
        } else {
          onHasMatchesChange?.(false)
        }
      } catch (err) {
        console.error('ChatInputSuggestions fetch error:', err)
        onHasMatchesChange?.(false)
      }
    }, 250)

    return () => clearTimeout(timeoutId)
  }, [token, onHasMatchesChange])

  if (suggestions.length === 0 || !activeToken) {
    return null
  }

  const handleSelect = (product: SuggestionItem) => {
    // Extract the first and second words from the title (e.g. "Kratom Rot | 50g..." -> "Kratom Rot")
    const words = product.title.split(/[\s|]+/).filter(Boolean)
    const selectedWords = words.slice(0, 2).join(' ') || product.title
    lastPickedRef.current = selectedWords
    const before = value.slice(0, start)
    const after = value.slice(end)
    const newText = before + selectedWords + (after.startsWith(' ') ? '' : ' ') + after
    setSuggestions([])
    setActiveToken('')
    onHasMatchesChange?.(false)
    onPick(newText, selectedWords)
  }

  return (
    <div className="mb-2 flex flex-wrap items-center gap-1.5 px-1 animate-in fade-in slide-in-from-bottom-1 duration-150">
      <span className="font-mono text-micro font-semibold uppercase tracking-wider text-sky-400 dark:text-sky-300 flex items-center gap-1 mr-0.5">
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-sky-400 animate-pulse shadow-[0_0_8px_rgba(56,189,248,0.8)]" />
        Treffer:
      </span>
      {suggestions.map((item) => (
        <button
          key={item.id}
          type="button"
          onMouseDown={(e) => {
            // Prevent input blur before click fires
            e.preventDefault()
          }}
          onClick={() => handleSelect(item)}
          className="cursor-pointer rounded-full border border-sky-400/50 bg-sky-950/50 dark:bg-sky-900/40 backdrop-blur-md px-3 py-1 font-mono text-xs font-medium text-sky-100 dark:text-sky-100 transition-all shadow-[0_0_12px_rgba(56,189,248,0.25)] hover:border-sky-300 hover:bg-sky-900/70 hover:shadow-[0_0_18px_rgba(56,189,248,0.45)] hover:scale-105 active:scale-95"
        >
          {item.title}
        </button>
      ))}
    </div>
  )
}
